import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"

/** Two people changed the same money field of a payment from the same value (needs a pick). */
export interface MoneyConflict {
  id: string
  transactionId: string
  field: "amount" | "date" | "type" | "accountId"
  /** The later edit: what the payment holds until a person picks. */
  shownValue: string | null
  otherValue: string | null
  shownBy: string | null
  otherBy: string | null
  transaction: { date: string; amount: string; description: string; merchantName: string | null }
}

export function useConflicts() {
  return useQuery({
    queryKey: ["conflicts"],
    queryFn: () => api.get<{ data: MoneyConflict[] }>("/transactions/conflicts"),
    staleTime: 60 * 1000,
  })
}

/** Pick one of the two values; the payment takes it. */
export function useResolveConflict() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: string | null }) =>
      api.post(`/transactions/conflicts/${id}/resolve`, { value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conflicts"] })
      qc.invalidateQueries({ queryKey: ["transactions"] })
      qc.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })
}

/** "Amount", "Date"… for a conflict's field. */
export const FIELD_LABEL: Record<MoneyConflict["field"], string> = {
  amount: "amount",
  date: "date",
  type: "type",
  accountId: "account",
}
